import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faLock, faArrowRight, faHome, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faGoogle, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { register } from '../services/authService';
import { useAuth } from '../context/AuthContext';

const SignupPage = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Calculate password strength
    if (name === 'password') {
      let strength = 0;
      if (value.length >= 8) strength += 1;
      if (/[A-Z]/.test(value)) strength += 1;
      if (/[0-9]/.test(value)) strength += 1;
      if (/[^A-Za-z0-9]/.test(value)) strength += 1;
      setPasswordStrength(strength);
    }
  };

  const validateForm = () => {
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const nextStep = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email) {
      setError('Please fill in all fields');
      return;
    }
    setError('');
    setStep(2);
  };

  const prevStep = (e) => {
    e.preventDefault();
    setError('');
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!formData.agreeTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const userData = {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword
      };

      const response = await register(userData);
      authLogin(response.user, response.token);
      navigate('/');
    } catch (err) {
      console.error('Registration error:', err);
      if (err.errors) {
        // Handle Laravel validation errors
        const errorMessages = Object.values(err.errors).flat();
        setError(errorMessages.join(', '));
      } else {
        setError(err.message || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0: return 'Very Weak';
      case 1: return 'Weak';
      case 2: return 'Medium';
      case 3: return 'Strong';
      case 4: return 'Very Strong';
      default: return '';
    }
  };

  const getPasswordStrengthClass = () => {
    switch (passwordStrength) {
      case 0: return 'very-weak';
      case 1: return 'weak';
      case 2: return 'medium';
      case 3: return 'strong';
      case 4: return 'very-strong';
      default: return '';
    }
  };

  return (
    <div className="auth-page signup-page">
      <div className="auth-container">
        <div className="row g-0 h-100">
          <div className="col-lg-6 auth-image-container">
            <div className="auth-image">
              <div className="overlay"></div>
              <div className="auth-quote">
                <h2>Join Our Community</h2>
                <p>"Home is where your story begins."</p>
              </div>
            </div>
          </div>
          <div className="col-lg-6 auth-form-container">
            <div className="auth-form-wrapper">
              <div className="auth-header">
                <Link to="/" className="auth-logo">
                  Real<span>Estate</span>
                </Link>
                <h1>Create Account</h1>
                <p>Sign up to get started with RealEstate</p>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="signup-progress">
                <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
                  <div className="step-number">1</div>
                  <div className="step-label">Account</div>
                </div>
                <div className="progress-line"></div>
                <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">Security</div>
                </div>
              </div>

              <form onSubmit={step === 1 ? nextStep : handleSubmit} className="auth-form">
                {step === 1 && (
                  <div className="step-content step-1 animate-in">
                    <div className="form-group">
                      <label htmlFor="fullName">
                        <FontAwesomeIcon icon={faUser} /> Full Name
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        className="form-control"
                        placeholder="Enter your full name"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">
                        <FontAwesomeIcon icon={faEnvelope} /> Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="social-auth">
                      <button type="button" className="btn btn-social btn-google">
                        <FontAwesomeIcon icon={faGoogle} /> Sign up with Google
                      </button>
                      <button type="button" className="btn btn-social btn-facebook">
                        <FontAwesomeIcon icon={faFacebook} /> Sign up with Facebook
                      </button>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-auth"
                    >
                      Continue <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div className="step-content step-2 animate-in">
                    <div className="form-group">
                      <label htmlFor="password">
                        <FontAwesomeIcon icon={faLock} /> Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        className="form-control"
                        placeholder="Create a password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                      {formData.password && (
                        <div className="password-strength">
                          <div className="strength-meter">
                            <div 
                              className={`strength-meter-fill ${getPasswordStrengthClass()}`}
                              style={{ width: `${passwordStrength * 25}%` }}
                            ></div>
                          </div>
                          <div className="strength-text">
                            {getPasswordStrengthText()}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">
                        <FontAwesomeIcon icon={faLock} /> Confirm Password
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        className="form-control"
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                      />
                      {formData.password && formData.confirmPassword && (
                        <div className={`password-match ${formData.password === formData.confirmPassword ? 'match' : 'no-match'}`}>
                          {formData.password === formData.confirmPassword ? (
                            <>
                              <FontAwesomeIcon icon={faCheck} /> Passwords match
                            </>
                          ) : (
                            'Passwords do not match'
                          )}
                        </div>
                      )}
                    </div>

                    <div className="form-check terms-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="agreeTerms"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onChange={handleChange}
                        required
                      />
                      <label className="form-check-label" htmlFor="agreeTerms">
                        I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
                      </label>
                    </div>

                    <div className="step-buttons">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={prevStep}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className={`btn btn-auth ${isLoading ? 'loading' : ''}`}
                        disabled={isLoading || !formData.agreeTerms || formData.password !== formData.confirmPassword}
                      >
                        {isLoading ? (
                          <span className="spinner"></span>
                        ) : (
                          <>
                            Create Account <FontAwesomeIcon icon={faArrowRight} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>

              <div className="auth-footer">
                <p>
                  Already have an account?{' '}
                  <Link to="/login" className="auth-link">
                    Sign In
                  </Link>
                </p>
                <Link to="/" className="back-to-home">
                  <FontAwesomeIcon icon={faHome} /> Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
